/**
 * AI消しゴム用のユーティリティ関数
 * 元画像の上に赤色で塗った部分を合成した画像を生成
 */

interface Line {
  head: 'circle' | 'triangle' | 'square'
  headSize: number
  points: number[]
  isAIEraser?: boolean
}

/**
 * AI消しゴムで塗った部分を赤色で塗りつぶした画像を生成
 * @param stage Konvaステージ
 * @param beforeImage 元画像
 * @param aiEraserLines AI消しゴムの線データ
 * @returns 赤色で塗った画像のBlob
 */
export async function generateAIEraserImage(
  stage: any,
  beforeImage: HTMLImageElement,
  aiEraserLines: Line[]
): Promise<Blob> {
  // 一時的なCanvasを作成
  const tempCanvas = document.createElement('canvas');
  const tempContext = tempCanvas.getContext('2d');

  if (!tempContext) {
    throw new Error('Canvas context取得失敗');
  }

  // 元画像の実サイズを使用（naturalWidthがない場合はwidthを使用）
  const imageWidth = beforeImage.naturalWidth || beforeImage.width;
  const imageHeight = beforeImage.naturalHeight || beforeImage.height;
  tempCanvas.width = imageWidth;
  tempCanvas.height = imageHeight;

  // ステージサイズとのスケール比を計算（座標変換用）
  const stageWidth = stage.width();
  const stageHeight = stage.height();
  const scaleX = imageWidth / stageWidth;
  const scaleY = imageHeight / stageHeight;

  // 1. 元画像を実サイズで描画
  tempContext.drawImage(beforeImage, 0, 0, imageWidth, imageHeight);

  // 2. AI消しゴムの線を不透明な赤色で上書き描画
  tempContext.strokeStyle = 'rgb(255, 0, 0)'; // 完全に不透明な赤色
  tempContext.fillStyle = 'rgb(255, 0, 0)';
  tempContext.globalCompositeOperation = 'source-over'; // 上書きモード

  aiEraserLines.forEach(line => {
    if (line.points.length < 2) return;

    if (line.head === 'circle') {
      // 円形の線を描画（座標とサイズをスケーリング）
      tempContext.lineWidth = line.headSize * scaleX;
      tempContext.lineCap = 'round';
      tempContext.lineJoin = 'round';

      tempContext.beginPath();
      for (let i = 0; i < line.points.length - 2; i += 2) {
        const scaledX = line.points[i] * scaleX;
        const scaledY = line.points[i + 1] * scaleY;

        if (i === 0) {
          tempContext.moveTo(scaledX, scaledY);
        } else {
          tempContext.lineTo(scaledX, scaledY);
        }
      }
      tempContext.stroke();
    } else if (line.head === 'square') {
      // 四角形の線を描画（座標とサイズをスケーリング）
      tempContext.lineWidth = 1;
      tempContext.lineCap = 'square';

      for (let i = 0; i < line.points.length - 2; i += 2) {
        const scaledX = line.points[i] * scaleX;
        const scaledY = line.points[i + 1] * scaleY;
        const scaledSize = line.headSize * scaleX;
        const x = scaledX - scaledSize / 2;
        const y = scaledY - scaledSize / 2;
        tempContext.fillRect(x, y, scaledSize, scaledSize);
      }
    } else if (line.head === 'triangle') {
      // 三角形の線を描画（座標とサイズをスケーリング）
      for (let i = 0; i < line.points.length - 2; i += 2) {
        const scaledCenterX = line.points[i] * scaleX;
        const scaledCenterY = line.points[i + 1] * scaleY;
        const scaledSize = (line.headSize / 2) * scaleX;

        tempContext.beginPath();
        tempContext.moveTo(scaledCenterX, scaledCenterY - scaledSize); // 上
        tempContext.lineTo(scaledCenterX - scaledSize, scaledCenterY + scaledSize); // 左下
        tempContext.lineTo(scaledCenterX + scaledSize, scaledCenterY + scaledSize); // 右下
        tempContext.closePath();
        tempContext.fill();
      }
    }
  });

  // 3. CanvasをBlobに変換（JPEG形式で圧縮してファイルサイズを削減）
  return new Promise((resolve, reject) => {
    tempCanvas.toBlob((blob) => {
      if (blob) {
        resolve(blob);
      } else {
        reject(new Error('Blob生成失敗'));
      }
    }, 'image/jpeg', 0.92); // JPEG形式、品質92%で圧縮（解像度は維持）
  });
}
